import type { BoardroomBrandGrid } from '../../../types';
import DataTable from './DataTable';
import { EnginesNote } from './engines';
import type { Column } from './tableColumns';

/**
 * Who goes after whom: a row per company whose own searches were looked at,
 * a column per rival seen on them, and each cell that rival's share of the
 * company's estimated clicks. Patterns no single report shows: one rival on
 * everyone's searches, or a company nobody bothers to target.
 */

type Row = BoardroomBrandGrid['rows'][number];

/** A share as a tinted cell: the darker, the more of the row's clicks. */
function Cell({ v }: { v: number | undefined }) {
  if (!v) return <span className="text-gray-300 dark:text-gray-600">·</span>;
  const tone =
    v >= 25
      ? 'bg-rose-200 text-rose-900 dark:bg-rose-900/60 dark:text-rose-100'
      : v >= 10
        ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
        : 'text-gray-600 dark:text-gray-300';
  return <span className={`rounded px-1 tabular-nums ${tone}`}>{v}%</span>;
}

export default function BrandGridReport({
  data,
  onOpen,
  roomName,
}: {
  data: BoardroomBrandGrid;
  onOpen: (itemId: number) => void;
  roomName?: string;
}) {
  if (!data.rows.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Nobody’s searches have been looked at yet. Open a company and press “Who’s going after its searches?” - each
        company you do that for becomes a row here.
      </div>
    );
  }
  const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline text-left';
  const columns: Column<Row>[] = [
    {
      key: 'title',
      label: 'Company',
      width: 16,
      always: true,
      value: r => r.title,
      cell: r => (
        <button type="button" onClick={() => onOpen(r.id)} className={LINK}>
          {r.title}
        </button>
      ),
    },
    { key: 'own', label: 'Own pages', width: 6, align: 'right', value: r => r.own, cell: r => `${r.own}%` },
    { key: 'rivals', label: 'Rivals', width: 6, align: 'right', value: r => r.rivals, cell: r => `${r.rivals}%` },
    { key: 'third', label: 'Third parties', width: 6, align: 'right', value: r => r.third_parties, cell: r => `${r.third_parties}%` },
    { key: 'paid', label: 'Rival ads', width: 5, align: 'right', value: r => r.paid_queries },
    ...data.cols.map(
      (c): Column<Row> => ({
        key: `rival-${c.id}`,
        label: c.name,
        width: 6,
        align: 'right',
        vertical: true,
        head: (
          <button type="button" onClick={() => onOpen(c.id)} className={LINK} title={`${c.name}: on ${c.targets} compan${c.targets === 1 ? 'y' : 'ies'}’ searches`}>
            {c.name}
          </button>
        ),
        value: r => r.cells[String(c.id)] ?? null,
        cell: r => <Cell v={r.cells[String(c.id)]} />,
      }),
    ),
  ];
  return (
    <div className="min-w-0 space-y-2 text-xs" data-testid="brand-grid">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Each row is a company whose own searches were looked at; each column a rival seen on them. A cell is that
        rival’s share of the company’s estimated clicks. Rivals that go after the most companies come first
        {data.more_cols ? `; ${data.more_cols} more not shown` : ''}.
      </p>
      <EnginesNote counts={data.engines} />
      <DataTable table="brand_grid" room={roomName} file="who-goes-after-whom" columns={columns} rows={data.rows} rowKey={r => `brand-row-${r.id}`} />
    </div>
  );
}
